import { useEffect, useState, useCallback, useRef } from 'react'
import api from '../../services/api'

/* ── Umbrales de alerta ── */
const UMBRALES = {
  cpu:   { warn: 70, crit: 90 },
  ram:   { warn: 75, crit: 90 },
  disco: { warn: 75, crit: 90 },
  swap:  { warn: 50, crit: 80 },
}

/* ── Helpers ── */
const fmtBytes = (b) => {
  if (!b || isNaN(b)) return '—'
  const gb = b / 1073741824
  if (gb >= 1) return `${gb.toFixed(2)} GB`
  const mb = b / 1048576
  if (mb >= 1) return `${mb.toFixed(1)} MB`
  return `${Math.round(b / 1024)} KB`
}
const fmtUptime = (s) => {
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60)
  return `${d}d ${h}h ${m}m`
}
const fmtMs   = (ms) => ms ? fmtUptime(Math.round((Date.now() - ms) / 1000)) : '—'
const pct     = (n)  => Math.min(Number(n) || 0, 100)
const COLOR   = (p)  => p > UMBRALES.disco.crit ? '#ef4444' : p > UMBRALES.disco.warn ? '#f59e0b' : '#22c55e'

/* ── Sistema de alertas ── */
function calcularAlertas(data) {
  if (!data) return []
  const alertas = []
  const { cpu, ram, swap, disco } = data

  if (cpu?.uso >= UMBRALES.cpu.crit)
    alertas.push({ nivel: 'critico', icono: '🔴', titulo: 'CPU crítico', msg: `Uso de CPU al ${cpu.uso}%. El servidor puede volverse lento o inaccesible.` })
  else if (cpu?.uso >= UMBRALES.cpu.warn)
    alertas.push({ nivel: 'aviso', icono: '🟡', titulo: 'CPU elevado', msg: `Uso de CPU al ${cpu.uso}%. Monitorea si sube más.` })

  if (ram?.percent >= UMBRALES.ram.crit)
    alertas.push({ nivel: 'critico', icono: '🔴', titulo: 'RAM crítica', msg: `${ram.percent}% de memoria usada (${fmtBytes(ram.libre)} libre). El sistema puede empezar a usar swap o matar procesos.` })
  else if (ram?.percent >= UMBRALES.ram.warn)
    alertas.push({ nivel: 'aviso', icono: '🟡', titulo: 'RAM elevada', msg: `${ram.percent}% de memoria usada. Considera revisar los procesos activos.` })

  if (swap?.total > 0 && swap?.percent >= UMBRALES.swap.crit)
    alertas.push({ nivel: 'critico', icono: '🔴', titulo: 'Swap saturado', msg: `${swap.percent}% de swap usado. Esto indica que la RAM está completamente llena y el sistema usa disco como memoria (muy lento).` })
  else if (swap?.total > 0 && swap?.percent >= UMBRALES.swap.warn)
    alertas.push({ nivel: 'aviso', icono: '🟡', titulo: 'Swap en uso', msg: `${swap.percent}% de swap activo. La RAM empieza a escasear.` })

  disco?.forEach(d => {
    const p = parseInt(d.percent)
    if (p >= UMBRALES.disco.crit)
      alertas.push({ nivel: 'critico', icono: '🔴', titulo: `Disco lleno: ${d.mount}`, msg: `${d.percent} usado (${fmtBytes(d.avail)} libre). ¡Puede causar fallos en el CRM, la BD y WAHA si llega al 100%!` })
    else if (p >= UMBRALES.disco.warn)
      alertas.push({ nivel: 'aviso', icono: '🟡', titulo: `Disco ${d.mount} al ${d.percent}`, msg: `Solo ${fmtBytes(d.avail)} libres. Considera limpiar logs o ampliar el disco pronto.` })
  })

  return alertas
}

/* ── Sub-components ── */
function SectionTitle({ icon, title, sub }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <h2 style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>{icon}</span>{title}
      </h2>
      {sub && <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>{sub}</p>}
    </div>
  )
}

function AlertaBanner({ alertas }) {
  if (!alertas.length) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 10, marginBottom: 16 }}>
      <span style={{ fontSize: 18 }}>✅</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: '#22c55e' }}>Todo en orden — todos los recursos están dentro de los límites normales</span>
    </div>
  )
  return (
    <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {alertas.map((a, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px',
          background: a.nivel === 'critico' ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)',
          border: `1px solid ${a.nivel === 'critico' ? 'rgba(239,68,68,0.35)' : 'rgba(245,158,11,0.35)'}`,
          borderRadius: 10, borderLeft: `4px solid ${a.nivel === 'critico' ? '#ef4444' : '#f59e0b'}`,
        }}>
          <span style={{ fontSize: 18, flexShrink: 0 }}>{a.icono}</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: a.nivel === 'critico' ? '#ef4444' : '#f59e0b', marginBottom: 3 }}>{a.titulo}</div>
            <div style={{ fontSize: 12, color: 'var(--text-soft)', lineHeight: 1.5 }}>{a.msg}</div>
          </div>
        </div>
      ))}
    </div>
  )
}

function GaugeRing({ percent, color, size = 90 }) {
  const r = (size - 12) / 2
  const c = 2 * Math.PI * r
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--border)" strokeWidth={10} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={10}
        strokeDasharray={c} strokeDashoffset={c * (1 - percent / 100)}
        strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
    </svg>
  )
}

function Gauge({ label, percent, sub, color }) {
  const clr = color || COLOR(percent)
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <GaugeRing percent={pct(percent)} color={clr} />
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: 15, fontWeight: 800, color: clr }}>
          {pct(percent)}%
        </div>
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginTop: 6 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

function Bar({ label, percent, color, right }) {
  const clr = color || COLOR(percent)
  return (
    <div style={{ marginBottom: 8 }}>
      {(label || right) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
          {label && <span style={{ color: 'var(--text-soft)', fontWeight: 500 }}>{label}</span>}
          {right && <span style={{ color: clr, fontWeight: 700 }}>{right}</span>}
        </div>
      )}
      <div style={{ height: 7, background: 'var(--border)', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct(percent)}%`, background: clr, borderRadius: 10, transition: 'width 0.6s ease' }} />
      </div>
    </div>
  )
}

function Dot({ ok }) {
  const c = ok ? '#22c55e' : '#ef4444'
  return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: c, boxShadow: `0 0 6px ${c}`, flexShrink: 0 }} />
}

function Tag({ label, color = 'var(--text-muted)', bg = 'var(--bg)' }) {
  return (
    <span style={{ fontSize: 10, fontWeight: 700, background: bg, color, padding: '2px 8px', borderRadius: 6, display: 'inline-block' }}>
      {label}
    </span>
  )
}

function Card({ children, style }) {
  return <div className="card" style={{ padding: 20, ...style }}>{children}</div>
}

function InfoRow({ label, value, hint }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
      <div>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-soft)' }}>{label}</span>
        {hint && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>{hint}</div>}
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', textAlign: 'right', maxWidth: '55%', wordBreak: 'break-word' }}>{value || '—'}</span>
    </div>
  )
}

/* ══════════════════════════════════════════
   SECCIÓN: Sesiones WhatsApp
══════════════════════════════════════════ */
function SesionesSection({ sesiones }) {
  const statusColor = { conectado: '#22c55e', conectando: '#f59e0b', desconectado: '#ef4444', baneado: '#dc2626' }
  const statusLabel = { conectado: '✅ Conectado', conectando: '🔄 Conectando', desconectado: '⭕ Desconectado', baneado: '🚫 Baneado' }

  return (
    <div style={{ marginBottom: 24 }}>
      <SectionTitle icon="📱" title="Sesiones de WhatsApp"
        sub={`${sesiones.length} número${sesiones.length !== 1 ? 's' : ''} registrados · Cada sesión corre en su propio contenedor Docker WAHA`} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 14 }}>
        {sesiones.map(s => {
          const stColor = statusColor[s.status] || '#9ca3af'
          const dockerOk = s.docker?.status?.startsWith('Up')
          const discoRw = s.docker?.disco?.sizeRw || 0
          const discoRoot = s.docker?.disco?.sizeRootFs || 0

          return (
            <div key={s.id} style={{
              background: 'var(--bg-card)', borderRadius: 14, padding: 18,
              border: `1px solid var(--border)`,
              borderLeft: `4px solid ${s.color || stColor}`,
              boxShadow: 'var(--shadow-sm)',
            }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: stColor, display: 'inline-block', boxShadow: `0 0 6px ${stColor}` }} />
                    <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--text)' }}>{s.nombre}</span>
                    {s.esPrincipal && <Tag label="Principal" color="#f59e0b" bg="rgba(245,158,11,0.12)" />}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', paddingLeft: 18 }}>
                    {s.telefono || 'Sin número'} · Empresa: {s.empresa}
                  </div>
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: stColor, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {statusLabel[s.status] || s.status}
                </div>
              </div>

              {/* Stats de mensajes */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                  Actividad de chats
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                  {[
                    { label: 'Chats',   val: s.stats.chatsTotal,    color: 'var(--text)', hint: 'Total' },
                    { label: 'Abiertos', val: s.stats.chatsAbiertos, color: '#3b82f6',    hint: 'En curso' },
                    { label: 'Msgs 30d', val: s.stats.mensajesMes,   color: '#8b5cf6',    hint: 'Este mes' },
                    { label: 'Msgs 7d',  val: s.stats.mensajesSemana,color: '#22c55e',    hint: 'Esta semana' },
                  ].map(({ label, val, color, hint }) => (
                    <div key={label} style={{ background: 'var(--bg)', borderRadius: 8, padding: '8px 6px', textAlign: 'center' }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color }}>{val?.toLocaleString() ?? '—'}</div>
                      <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.2 }}>{hint}</div>
                      <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Docker stats */}
              {s.docker ? (
                <div style={{ background: 'var(--bg)', borderRadius: 10, padding: '10px 12px', border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <Dot ok={dockerOk} />
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)' }}>🐳 {s.docker.nombre}</span>
                    <span style={{ fontSize: 10, color: dockerOk ? '#22c55e' : '#ef4444', marginLeft: 'auto' }}>{s.docker.status}</span>
                  </div>

                  {/* Tamaño en disco */}
                  <div style={{ marginBottom: 8, padding: '6px 8px', background: 'var(--bg-card)', borderRadius: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>💾 Espacio en disco</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 13, fontWeight: 800, color: '#3b82f6' }}>{fmtBytes(discoRw)}</div>
                        <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>Datos propios</div>
                        <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>(sesión + mensajes)</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 13, fontWeight: 800, color: '#8b5cf6' }}>{fmtBytes(discoRoot)}</div>
                        <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>Total imagen</div>
                        <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>(incluye WAHA)</div>
                      </div>
                    </div>
                  </div>

                  {/* CPU/RAM en tiempo real */}
                  {s.docker.stats && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                      {[
                        { label: 'CPU ahora',  val: s.docker.stats.cpu,      hint: '% del servidor' },
                        { label: 'RAM %',       val: s.docker.stats.mem_perc, hint: 'del total' },
                        { label: 'RAM uso',     val: s.docker.stats.mem?.split('/')[0]?.trim(), hint: 'memoria viva' },
                        { label: 'Red ↓↑',     val: s.docker.stats.net_io,   hint: 'datos transferidos' },
                        { label: 'Disco I/O',  val: s.docker.stats.block_io, hint: 'lectura/escritura' },
                        { label: 'Procesos',   val: s.docker.stats.pids,     hint: 'threads activos' },
                      ].map(({ label, val, hint }) => (
                        <div key={label} style={{ textAlign: 'center', background: 'var(--bg-card)', borderRadius: 6, padding: '5px 4px' }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', wordBreak: 'break-all', lineHeight: 1.2 }}>{val || '—'}</div>
                          <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>{label}</div>
                          <div style={{ fontSize: 8, color: 'var(--text-muted)', opacity: 0.7 }}>{hint}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', padding: '8px 0',
                  background: 'var(--bg)', borderRadius: 8 }}>
                  ⚠️ Sin contenedor Docker "waha" detectado
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ══════════════════════════════════════════
   PÁGINA PRINCIPAL
══════════════════════════════════════════ */
export default function AdminVPS() {
  const [data, setData]   = useState(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [lastUpdate, setLastUpdate] = useState(null)
  const [tab, setTab] = useState('overview')
  const alertasMostradas = useRef(new Set())

  const fetchStats = useCallback(async () => {
    try {
      const { data: d } = await api.get('/admin/sa/vps')
      setData(d)
      setLastUpdate(new Date())
      setError('')

      // Notificaciones del navegador para alertas críticas
      const alertas = calcularAlertas(d)
      const criticas = alertas.filter(a => a.nivel === 'critico')
      if (criticas.length && Notification.permission === 'granted') {
        criticas.forEach(a => {
          const key = a.titulo
          if (!alertasMostradas.current.has(key)) {
            alertasMostradas.current.add(key)
            new Notification(`⚠️ MixCRM VPS — ${a.titulo}`, { body: a.msg, icon: '/logo192.png' })
            setTimeout(() => alertasMostradas.current.delete(key), 300000) // re-alertar en 5 min
          }
        })
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Error al obtener estadísticas del VPS')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    // Pedir permiso para notificaciones al cargar
    if (Notification.permission === 'default') Notification.requestPermission()
    fetchStats()
    const iv = setInterval(fetchStats, 10000)
    return () => clearInterval(iv)
  }, [fetchStats])

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, flexDirection: 'column', gap: 16 }}>
      <div className="spinner" style={{ width: 40, height: 40 }} />
      <span style={{ color: 'var(--text-muted)' }}>Conectando con el VPS...</span>
    </div>
  )
  if (error) return (
    <div style={{ padding: 32 }}>
      <Card style={{ border: '1px solid var(--danger)', textAlign: 'center', padding: 40 }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
        <p style={{ color: 'var(--danger)', fontWeight: 600 }}>{error}</p>
        <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={fetchStats}>Reintentar</button>
      </Card>
    </div>
  )

  const { sistema, cpu, ram, swap, disco, docker_df, red, servicios, procesos_top, base_de_datos, sesiones } = data
  const alertas = calcularAlertas(data)

  const TABS = [
    { id: 'overview',  label: '📊 General'   },
    { id: 'sesiones',  label: `📱 Sesiones (${sesiones?.length || 0})` },
    { id: 'servicios', label: '⚙️ Servicios'  },
    { id: 'procesos',  label: '🔄 Procesos'   },
    { id: 'bd',        label: '🗄️ Base de datos' },
  ]

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1280, margin: '0 auto' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>🖥️ Monitor VPS</h1>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
            {sistema.hostname} · {sistema.os} · Activo hace: {sistema.uptime_str || fmtUptime(sistema.uptime_s)}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {alertas.length > 0 && (
            <div style={{
              background: alertas.some(a => a.nivel === 'critico') ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)',
              color: alertas.some(a => a.nivel === 'critico') ? '#ef4444' : '#f59e0b',
              border: `1px solid ${alertas.some(a => a.nivel === 'critico') ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)'}`,
              borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 700,
            }}>
              ⚠️ {alertas.length} alerta{alertas.length !== 1 ? 's' : ''}
            </div>
          )}
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {lastUpdate && `Actualizado ${lastUpdate.toLocaleTimeString()}`}
          </span>
          <button className="btn btn-sm btn-primary" onClick={fetchStats}>🔄 Ahora</button>
        </div>
      </div>

      {/* ── Banner de alertas ── */}
      <AlertaBanner alertas={alertas} />

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            border: 'none', background: 'none', borderRadius: '8px 8px 0 0',
            color: tab === t.id ? 'var(--primary)' : 'var(--text-muted)',
            borderBottom: tab === t.id ? `2px solid var(--primary)` : '2px solid transparent',
            transition: 'all 0.15s',
          }}>{t.label}</button>
        ))}
      </div>

      {/* ══ TAB: GENERAL ══ */}
      {tab === 'overview' && (
        <>
          {/* Row 1: Gauges */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 16 }}>

            <Card style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>CPU</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 10 }}>Porcentaje del procesador en uso ahora mismo</div>
              <Gauge label={`${cpu.cores} núcleos`} percent={cpu.uso} sub={cpu.model?.slice(0,28)} />
              <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-muted)' }}>
                Carga promedio: <b style={{ color: 'var(--text)' }}>{cpu.loadavg[0]}</b> / {cpu.loadavg[1]} / {cpu.loadavg[2]}
                <div style={{ fontSize: 10, opacity: 0.7 }}>últimos 1m / 5m / 15m</div>
              </div>
            </Card>

            <Card style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>RAM</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 10 }}>Memoria física usada por todos los procesos</div>
              <Gauge label="Memoria" percent={ram.percent} sub={`${fmtBytes(ram.usado)} / ${fmtBytes(ram.total)}`} color="#3b82f6" />
              <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-muted)' }}>
                Libre: <b style={{ color: '#22c55e' }}>{fmtBytes(ram.libre)}</b>
              </div>
            </Card>

            <Card style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>SWAP</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 10 }}>Disco usado como memoria de emergencia cuando la RAM se llena</div>
              <Gauge label="Swap" percent={swap?.percent || 0}
                sub={swap?.total > 0 ? `${fmtBytes(swap.used)} / ${fmtBytes(swap.total)}` : 'Sin swap'}
                color={swap?.percent > 50 ? '#ef4444' : '#8b5cf6'} />
              {swap?.total === 0 && <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-muted)' }}>Sin swap configurado</div>}
            </Card>

            <Card>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Red</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 10 }}>Datos transferidos por cada interfaz de red desde el arranque</div>
              {red.interfaces?.slice(0, 3).map(ifc => (
                <div key={ifc.iface} style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{ifc.iface}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>↓ {fmtBytes(ifc.rx_bytes)} recibidos</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>↑ {fmtBytes(ifc.tx_bytes)} enviados</div>
                </div>
              ))}
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)', fontSize: 11, color: 'var(--text-muted)' }}>
                Conexiones TCP activas: <b style={{ color: 'var(--text)' }}>{red.tcp_establecidas}</b>
              </div>
            </Card>
          </div>

          {/* Row 2: Disco */}
          <Card style={{ marginBottom: 16 }}>
            <SectionTitle icon="💾" title="Almacenamiento"
              sub="Espacio en disco de las particiones del servidor (solo particiones reales, sin Docker virtual)" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14, marginBottom: 16 }}>
              {disco.map((d, i) => {
                const p = typeof d.percent === 'number' ? d.percent : parseInt(d.percent)
                return (
                  <div key={i} style={{ background: 'var(--bg)', borderRadius: 10, padding: '14px', border: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{d.mount}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{d.fs}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: COLOR(p) }}>{p}%</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{fmtBytes(d.used)} / {fmtBytes(d.size)}</div>
                      </div>
                    </div>
                    <Bar percent={p} />
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                      Libre: <b style={{ color: COLOR(100 - p) }}>{fmtBytes(d.avail)}</b>
                    </div>
                    {p >= UMBRALES.disco.warn && (
                      <div style={{ fontSize: 10, color: p >= UMBRALES.disco.crit ? '#ef4444' : '#f59e0b', marginTop: 6, fontWeight: 600 }}>
                        {p >= UMBRALES.disco.crit ? '🔴 ¡Disco casi lleno!' : '🟡 Espacio bajo'}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Docker storage summary */}
            {docker_df && Object.keys(docker_df).length > 0 && (
              <div style={{ background: 'var(--bg)', borderRadius: 10, padding: '12px 16px', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
                  🐳 Espacio usado por Docker en total
                </div>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                  {Object.entries(docker_df).map(([tipo, info]) => (
                    <div key={tipo} style={{ fontSize: 12 }}>
                      <span style={{ fontWeight: 700, color: 'var(--text)' }}>{info.size}</span>
                      <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>
                        en {tipo === 'Images' ? 'Imágenes' : tipo === 'Containers' ? 'Contenedores' : tipo === 'Local Volumes' ? 'Volúmenes' : tipo}
                      </span>
                      {info.reclaimable && info.reclaimable !== '0B' && (
                        <span style={{ color: '#22c55e', marginLeft: 4, fontSize: 11 }}>({info.reclaimable} recuperables)</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Row 3: Load average */}
          <Card>
            <SectionTitle icon="📈" title="Carga del sistema"
              sub="Promedio de procesos esperando CPU. Valores normales: menos que el número de núcleos del servidor" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
              {[['Último minuto', cpu.loadavg[0]], ['Últimos 5 min', cpu.loadavg[1]], ['Últimos 15 min', cpu.loadavg[2]]].map(([label, val]) => {
                const p = Math.min(Math.round((parseFloat(val) / cpu.cores) * 100), 100)
                return (
                  <div key={label} style={{ background: 'var(--bg)', borderRadius: 10, padding: '14px 16px', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>{label}</div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: COLOR(p), marginBottom: 8 }}>{val}</div>
                    <Bar percent={p} />
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
                      {p}% de capacidad · {p > 100 ? '⚠️ Saturado' : p > 70 ? '🟡 Elevado' : '✅ Normal'}
                    </div>
                  </div>
                )
              })}
            </div>
          </Card>
        </>
      )}

      {/* ══ TAB: SESIONES ══ */}
      {tab === 'sesiones' && sesiones && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Total sesiones',   val: sesiones.length,                                        color: 'var(--text)', hint: 'números WhatsApp' },
              { label: 'Conectadas',        val: sesiones.filter(s => s.status === 'conectado').length,  color: '#22c55e',     hint: 'bot activo' },
              { label: 'Desconectadas',     val: sesiones.filter(s => s.status === 'desconectado').length, color: '#ef4444',   hint: 'requieren atención' },
              { label: 'Msgs / 30 días',    val: sesiones.reduce((a,s) => a + (s.stats.mensajesMes||0), 0).toLocaleString(), color: '#8b5cf6', hint: 'todos los números' },
            ].map(s => (
              <Card key={s.label} style={{ textAlign: 'center', padding: 16 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.val}</div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', marginTop: 4 }}>{s.label}</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{s.hint}</div>
              </Card>
            ))}
          </div>
          <SesionesSection sesiones={sesiones} />
        </>
      )}

      {/* ══ TAB: SERVICIOS ══ */}
      {tab === 'servicios' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

          <Card>
            <SectionTitle icon="⚡" title="Procesos PM2"
              sub="PM2 mantiene vivo el backend del CRM. Si se cae, lo reinicia automáticamente." />
            {servicios.pm2.length === 0
              ? <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Sin procesos PM2</p>
              : servicios.pm2.map((p, i) => (
                <div key={i} style={{ background: 'var(--bg)', borderRadius: 10, padding: '12px 14px', marginBottom: 8, border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Dot ok={p.status === 'online'} />
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{p.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          PID {p.pid} · v{p.version} · Activo hace {fmtMs(p.uptime)}
                        </div>
                        <div style={{ fontSize: 10, color: p.restarts > 5 ? '#f59e0b' : 'var(--text-muted)' }}>
                          {p.restarts} reinicios {p.restarts > 5 ? '⚠️ muchos reinicios' : ''}
                        </div>
                      </div>
                    </div>
                    <Tag label={p.status} color={p.status === 'online' ? '#22c55e' : '#ef4444'} bg={p.status === 'online' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)'} />
                  </div>
                  <Bar label="CPU" right={`${p.cpu}%`} percent={pct(p.cpu)} color="#3b82f6" />
                  <Bar label="Memoria RAM" right={fmtBytes(p.memory)} percent={pct((p.memory / 536870912) * 100)} color="#8b5cf6" />
                </div>
              ))
            }
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>🌐 Nginx</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 10 }}>Proxy inverso que dirige el tráfico web al CRM. Si se cae, la app no carga.</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--bg)', borderRadius: 10, border: '1px solid var(--border)' }}>
                <Dot ok={servicios.nginx === 'activo'} />
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', flex: 1 }}>nginx</span>
                <Tag label={servicios.nginx} color={servicios.nginx === 'activo' ? '#22c55e' : '#ef4444'} bg={servicios.nginx === 'activo' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)'} />
              </div>
            </div>
          </Card>

          <Card>
            <SectionTitle icon="🐳" title={`Contenedores Docker (${servicios.docker.length})`}
              sub="Cada número WhatsApp corre en su propio contenedor WAHA. Más números = más contenedores." />
            {servicios.docker.length === 0
              ? <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Sin contenedores activos</p>
              : servicios.docker.map((c, i) => {
                const up = c.status?.startsWith('Up')
                return (
                  <div key={i} style={{ background: 'var(--bg)', borderRadius: 10, padding: '12px 14px', marginBottom: 8, border: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                      <Dot ok={up} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{c.image}</div>
                        <div style={{ fontSize: 10, color: up ? '#22c55e' : '#ef4444' }}>{c.status}</div>
                      </div>
                      <Tag label={up ? 'Corriendo' : 'Detenido'} color={up ? '#22c55e' : '#ef4444'} bg={up ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)'} />
                    </div>
                    {c.disco && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
                        💾 Datos propios: <b style={{ color: 'var(--text)' }}>{fmtBytes(c.disco.sizeRw)}</b> ·
                        Total con imagen: <b style={{ color: 'var(--text)' }}>{fmtBytes(c.disco.sizeRootFs)}</b>
                      </div>
                    )}
                    {c.stats && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
                        {[['CPU', c.stats.cpu], ['RAM %', c.stats.mem_perc], ['RAM', c.stats.mem?.split('/')[0]?.trim()],
                          ['Red I/O', c.stats.net_io], ['Disco I/O', c.stats.block_io], ['PIDs', c.stats.pids]].map(([l, v]) => (
                          <div key={l} style={{ background: 'var(--bg-hover)', borderRadius: 6, padding: '5px 6px', textAlign: 'center' }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)' }}>{v || '—'}</div>
                            <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>{l}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })
            }
          </Card>
        </div>
      )}

      {/* ══ TAB: PROCESOS ══ */}
      {tab === 'procesos' && (
        <Card>
          <SectionTitle icon="🔄" title="Top procesos del sistema"
            sub="Los 10 procesos que más CPU consumen ahora mismo. Útil para detectar procesos que se disparan." />
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  {['Usuario', 'PID', 'CPU %', 'MEM %', 'Estado', 'Tiempo CPU', 'Proceso'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--text-muted)', fontWeight: 600, fontSize: 11, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {procesos_top?.map((p, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 10px', color: 'var(--text-soft)' }}>{p.user}</td>
                    <td style={{ padding: '8px 10px', fontFamily: 'monospace', color: 'var(--text)' }}>{p.pid}</td>
                    <td style={{ padding: '8px 10px' }}>
                      <span style={{ color: parseFloat(p.cpu) > 20 ? '#ef4444' : parseFloat(p.cpu) > 5 ? '#f59e0b' : 'var(--text)', fontWeight: 600 }}>
                        {p.cpu}%
                      </span>
                    </td>
                    <td style={{ padding: '8px 10px', color: 'var(--text)' }}>{p.mem}%</td>
                    <td style={{ padding: '8px 10px' }}>
                      <Tag label={p.stat || '—'} color="var(--text-soft)" />
                    </td>
                    <td style={{ padding: '8px 10px', color: 'var(--text-muted)', fontFamily: 'monospace' }}>{p.time}</td>
                    <td style={{ padding: '8px 10px', color: 'var(--text)', fontFamily: 'monospace', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.cmd}>{p.cmd}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ══ TAB: BASE DE DATOS ══ */}
      {tab === 'bd' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Card>
            <SectionTitle icon="🗄️" title="PostgreSQL"
              sub="Base de datos del CRM. Guarda todos los chats, contactos, mensajes, usuarios y configuración." />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              <div style={{ background: 'var(--bg)', borderRadius: 10, padding: 14, textAlign: 'center', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#3b82f6' }}>{base_de_datos?.size || '—'}</div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', marginTop: 4 }}>Tamaño total</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>espacio en disco</div>
              </div>
              <div style={{ background: 'var(--bg)', borderRadius: 10, padding: 14, textAlign: 'center', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#22c55e' }}>{base_de_datos?.conexiones ?? '—'}</div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', marginTop: 4 }}>Conexiones activas</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>consultas en curso</div>
              </div>
            </div>
          </Card>

          <Card>
            <SectionTitle icon="📋" title="Tablas más grandes"
              sub="Las tablas que más espacio ocupan. 'mensajes' y 'chats' crecen con el tiempo." />
            {base_de_datos?.tablas?.length > 0
              ? base_de_datos.tablas.map((t, i) => (
                <InfoRow key={i} label={t.tabla} value={t.size}
                  hint={t.tabla === 'mensajes' ? 'Crece con cada mensaje recibido/enviado' : t.tabla === 'chats' ? 'Conversaciones del CRM' : t.tabla === 'contactos' ? 'Clientes registrados' : ''} />
              ))
              : <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Sin datos (verifica conexión a DB)</p>
            }
          </Card>
        </div>
      )}

    </div>
  )
}
